import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { size } from 'lodash';
import useClipboardValue from 'hooks/useClipboardValue';
import Keyboard from 'containers/Keyboard';
import { StyledKeyboardWrapper } from './FormStyledComponents';
import ActionsSection from './ActionsSection';

const MAX_PASSWORD_LENGTH = 20;

const TIMEOUT_MS = 500;

const ALL_SYMBOL_REG_EXP = /[\S\s]/g;

interface IPasswordForm {
  placeholder?: string;
  invalidMessage: string;
  buttonLabel: string;
  sectionName?: string;
  onSubmit: (password: string) => void;
}

const PasswordForm: React.FC<IPasswordForm> = (props) => {
  const { placeholder, invalidMessage, buttonLabel, sectionName, onSubmit, children } = props;
  const [password, setPassword] = useState('');
  const passValueRef = useRef('');
  const [mask, setMask] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [isValid, setIsValid] = useState(true);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  const showKeyboard = useCallback(() => setIsKeyboardVisible(true), []);
  const hideKeyboard = useCallback(() => setIsKeyboardVisible(false), []);
  const onPasswordUpdate = useCallback((value) => setPassword(value), []);
  const onCursorPositionUpdate = useCallback((value) => setCursorPosition(value), []);
  const onButtonClick = useCallback(() => {
    if (password) {
      onSubmit(password);
    } else {
      setIsValid(false);
    }
  }, [password, onSubmit]);

  useClipboardValue((value) => {
    if (value.length > MAX_PASSWORD_LENGTH) return;
    setPassword(value);
    setCursorPosition(value.length);
    setIsKeyboardVisible(false);
  });

  useEffect(() => {
    let mask = '';
    // if removing no need to show last symbol
    if (size(passValueRef.current) > size(password)) {
      mask = password.replace(ALL_SYMBOL_REG_EXP, '*');
    } else {
      mask = password.slice(0, -1).replace(ALL_SYMBOL_REG_EXP, '*') + password.slice(-1);
    }

    passValueRef.current = password;

    setMask(mask);

    const timeout = setTimeout(() => {
      setMask((mask) => mask.replace(ALL_SYMBOL_REG_EXP, '*'));
    }, TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [password]);

  return (
    <>
      <ActionsSection id="" sectionName={sectionName} defaultSection>
        <ActionsSection.Input
          value={mask}
          cursor={isKeyboardVisible}
          cursorPosition={cursorPosition}
          placeholder={placeholder}
          isValid={isValid}
          invalidMessage={invalidMessage}
          onEnter={showKeyboard}
        />
        <ActionsSection.Button label={buttonLabel} onEnter={onButtonClick} />
        {children}
      </ActionsSection>
      {isKeyboardVisible && (
        <StyledKeyboardWrapper>
          <Keyboard
            onQueryUpdated={onPasswordUpdate}
            onCursorPositionUpdate={onCursorPositionUpdate}
            onLeave={hideKeyboard}
            query={password}
            maxQuerySize={MAX_PASSWORD_LENGTH}
            selectedLanguage="en"
            type="noEmbeddedInput"
          />
        </StyledKeyboardWrapper>
      )}
    </>
  );
};

PasswordForm.defaultProps = {
  sectionName: 'CONTRACT_NUMBER_INPUT',
};

PasswordForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  buttonLabel: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  invalidMessage: PropTypes.string,
  sectionName: PropTypes.string,
};

export default PasswordForm;
